/**
 * EventsRepFilter — Smart Admin OI-10 (2026-06-20).
 *
 * Typeahead picker for the Events list page's Lead/Event Rep, Territory Manager,
 * Regional Manager and Sales Director filters. Replaces the requested (and never
 * shipped) free-text rep search with a name dropdown + typeahead, alphabetical by
 * FIRST NAME — reusing the same display/sort helpers and the tier→position_type
 * mapping the event-create Sales Hierarchy section uses, so the filter choices
 * match what operators see when assigning the event.
 *
 * `allowedTypes` selects which sales_positions tiers populate the dropdown:
 *   Lead/Event Rep → [sales_rep, territory_manager, regional_manager, director]
 *   Territory Manager → [territory_manager]
 *   Regional Manager → [regional_manager]
 *   Sales Director → [director]
 * The parent persists only the selected position id in the URL; the component
 * resolves the display label by id so it stays accurate after a rename/reload.
 */

import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  getSalesPositions,
  getSalesPosition,
} from "../../services/salesTeam.service";
import {
  displaySalesPositionName,
  sortSalesPositionsByFirstName,
} from "../../utils/salesPositionDisplay";

interface PositionRow {
  id: string;
  assigned_name: string | null;
  title: string | null;
  position_type: string;
  is_active?: boolean;
}

interface Props {
  label: string;
  value: string;
  onChange: (id: string) => void;
  allowedTypes: string[];
  placeholder?: string;
}

const EventsRepFilter: React.FC<Props> = ({
  label,
  value,
  onChange,
  allowedTypes,
  placeholder = "Anyone",
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  // Stable key for the tier set so the four instances don't share a cache entry.
  const typesKey = [...allowedTypes].sort().join(",");

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", onClick);
      return () => document.removeEventListener("mousedown", onClick);
    }
  }, [open]);

  // Active positions for this tier (shared getSalesPositions service so the
  // fetch shape stays in lockstep with the rest of the app). The sales team is
  // small (well under the PostgREST 1000-row cap), so we filter the term
  // client-side.
  const { data: positions = [], isLoading } = useQuery({
    queryKey: ["events-rep-filter", typesKey],
    queryFn: async () => {
      const rows = await getSalesPositions({
        position_type: allowedTypes,
        is_active: true,
      });
      return sortSalesPositionsByFirstName(rows as unknown as PositionRow[]);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Resolve the selected label by id — covers a value that isn't in the active
  // tier list (e.g. a position later marked inactive) so the chip never goes blank.
  const { data: selectedById } = useQuery({
    queryKey: ["events-rep-filter-selected", value],
    queryFn: async () => {
      if (!value) {
        return null;
      }
      const sp = await getSalesPosition(value);
      return (sp as unknown as PositionRow | null) ?? null;
    },
    enabled: !!value,
    staleTime: 5 * 60 * 1000,
  });

  const selected = value
    ? (positions.find((p) => p.id === value) ?? selectedById ?? null)
    : null;
  const selectedLabel = selected
    ? displaySalesPositionName(selected, { markInactive: true })
    : "";

  const t = term.trim().toLowerCase();
  const results = t
    ? positions.filter((p) =>
        displaySalesPositionName(p).toLowerCase().includes(t),
      )
    : positions;

  // Don't render a dead control: if this tier has no positions to choose from
  // (e.g. PROD has no TM/RM/SD rows yet, or the viewer's RLS denies
  // sales_positions reads) and nothing is selected, hide the filter entirely.
  // A filter with an active selection always renders so it can be seen/cleared.
  if (!value && !isLoading && positions.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 relative" ref={ref}>
      <label className="text-sm font-medium text-gray-700">{label}:</label>
      <div className="relative">
        <div className="flex items-center gap-1 border border-gray-300 rounded-lg pl-2 pr-1 py-1 bg-white">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={value ? selectedLabel : term}
            onChange={(e) => {
              if (value) {
                onChange("");
              }
              setTerm(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="px-1 py-0.5 text-sm w-44 outline-none"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setTerm("");
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
              title={`Clear ${label} filter`}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
            {results.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-500">No matches</p>
            )}
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange(p.id);
                  setTerm("");
                  setOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
              >
                {displaySalesPositionName(p, { markInactive: true })}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsRepFilter;
